<template>
  <CardContainer>
    <v-container class="sign-up-container">
      <v-layout row wrap>
        <v-flex xs12>
          <CardTitle>Create your HomeGauge account</CardTitle>
        </v-flex>
      </v-layout>
      <v-flex xs12>
        <div class="sub-header-container">
          <div class="sub-title">Already have a HomeGauge account?</div>
          <div class="login-title" @click="redirectToLogin()">Log In</div>
        </div>
        <div class="divider-line-top"></div>
      </v-flex>
      <form class="form-container" novalidate>
        <v-layout row wrap id="messages">
          <v-flex xs12>
            <BaseAlert
              v-bind:class="showApiErrors ? 'error-alerts' : ''"
              :hg-value="showApiErrors"
              hg-type="error"
              ref="api-error-alert"
              v-if="showApiErrors">
              <div>
                <div v-if="error.message">
                  <div v-if="error.fieldViolations.length === 0">
                    {{error.message}}
                  </div>
                  <div
                    v-else
                    v-for="fieldViolation in error.fieldViolations"
                    :key="fieldViolation.field">
                    <div v-if="fieldViolation.field === 'username'">
                     The entered username is already associated with an account.
                    </div>
                    <div v-else>
                      <div
                        class="error-message"
                        v-for="(message, index) in fieldViolation.messages"
                        :key="`${fieldViolation.field}_${index}`">
                        {{message}}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="showExistingUserError">
                  <ul>
                      {{existingUserError}}
                  </ul>
                </div>
              </div>
            </BaseAlert>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12>
            <div class="form-title">CONTACT INFO</div>
          </v-flex>
          <v-flex xs6>
            <TextInput
              class="input-holder pr-2"
              v-model="model.user.givenName"
              :validations="inputValidations.firstName"
              display-name="First Name"
              id="firstName"
              type="string" />
          </v-flex>
          <v-flex xs6>
            <TextInput
              class="input-holder pl-2"
              v-model="model.user.familyName"
              :validations="inputValidations.lastName"
              display-name="Last Name"
              id="lastName"
              type="string" />
          </v-flex>
          <v-flex xs12>
            <TextInput
              class="input-holder"
              v-model="model.user.email"
              :validations="inputValidations.email"
              display-name="Email"
              id="email"
              type="email"
              :error="existingUserError"
              v-on:blur="isUserEmailExists()"/>
          </v-flex>
          <v-flex xs6 sm8>
            <TextInput
              class="pr-2 input-holder"
              v-model="model.user.phone.number"
              :validations="{ ...inputValidations.phone, min: 10, required: true }"
              display-name="Phone Number"
              id="phoneNumber"
              mask="###-###-####"/>
          </v-flex>
          <v-flex xs6 sm4>
            <SelectInput
              v-model="model.user.phone.type"
              class="pl-2 input-holder"
              :items="Phone"
              :validations="{ required: true }"
              display-name="Phone Type">
            </SelectInput>
          </v-flex>
          <v-flex xs12>
            <TextInput
              class="input-holder"
              v-model="model.user.companyName"
              :validations="{ ...inputValidations.company, required: true }"
              display-name="Company Name"
              id="companyName"/>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12>
            <div class="form-title">ADDRESS</div>
          </v-flex>
          <v-flex xs12>
            <TextInput
              class="input-holder"
              v-model="model.user.address1"
              :validations="{ ...inputValidations.address1, required: true }"
              display-name="Address 1"
              id="address1"/>
          </v-flex>
          <v-flex xs12>
            <TextInput
              class="input-holder"
              v-model="model.user.address2"
              :validations="inputValidations.address2"
              display-name="Address 2 (Optional)"
              id="address2"/>
          </v-flex>
          <v-flex xs6>
            <TextInput
              class="input-holder pr-2"
              v-model="model.user.city"
              :validations="inputValidations.city"
              display-name="City"
              id="city"/>
          </v-flex>
          <v-flex xs6>
            <TextInput
              class="input-holder pl-2"
              v-model="model.user.state"
              :validations="{ ...inputValidations.state, max: 2, required: true }"
              id="state"
              maxlength="2"
              display-name="State"/>
          </v-flex>
          <v-flex xs6>
            <TextInput
              class="input-holder pr-2"
              v-model="model.user.postalCode"
              :validations="{ ...inputValidations.postalCode, required: true }"
              display-name="ZIP/Postal Code"
              id="zip"/>
          </v-flex>
          <v-flex xs6>
            <SelectInput
              v-model="model.user.country"
              class="pl-2 input-holder"
              :items="Country"
              :validations="{ required: true }"
              display-name="Country">
            </SelectInput>
          </v-flex>

        </v-layout>
        <v-layout row wrap>
          <v-flex xs12>
            <div class="form-title">ACCOUNT</div>
          </v-flex>
          <v-flex xs12>
            <TextInput
              class="input-holder"
              v-model="model.user.username"
              :validations="{...inputValidations.userName, required: true, max: 25}"
              display-name="User Name"
              id="address1"/>
          </v-flex>
          <v-flex xs6>
            <PasswordValidationField
              v-model="model.user.verifyPassword"
              :validate="{ confirmed: model.user.password }"
              autocomplete="new-password"
              id="password"
              label="Password"
              data-vv-name="Password"
              name="password"
              class="pr-2 input-holder"
              flat
              box />
          </v-flex>
          <v-flex xs6>
            <PasswordField
              v-model="model.user.password"
              v-validate="inputValidations.password"
              id="password-confirm"
              name="password-confirm"
              data-vv-name="Confirm Password"
              label="Confirm Password"
              class="pl-2 confirm-password input-holder"
              flat
              box />
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12>
            <div class="divider-line"></div>
          </v-flex>
          <v-flex xs12 class="button-container">
            <BaseButton
              type="submit"
              class="continue-button"
              :hgLabel="'Continue To Purchase'"
              @click="onFormSubmit($event)"
              :disabled="isFormInvalid"/>
          </v-flex>
        </v-layout>

      </form>
    </v-container>
  </CardContainer>
</template>

<script>
import { Components, Constants } from '@spectorasoftware/homegauge-vue';
import CardContainer from '@/components/CardContainer.vue';
import CardTitle from '@/components/CardTitle.vue';
import TextInput from '@/components/Shared/TextInput.vue';
import SelectInput from '@/components/Shared/SelectInput.vue';

const {
  BaseAlert, PasswordField, PasswordValidationField,
} = Components;
const { InputValidations, AccountTypes } = Constants;
const { BaseButton } = Components;

const defaultModel = {
  user: {
    givenName: '',
    familyName: '',
    email: '',
    phone: {
      number: '',
      type: '',
    },
    companyName: '',
    username: '',
    password: '',
    verifyPassword: '',
    token: '',
    accountType: 'INSPECTOR',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },
};

export default {
  components: {
    BaseButton,
    BaseAlert,
    CardContainer,
    CardTitle,
    PasswordField,
    PasswordValidationField,
    TextInput,
    SelectInput,
  },
  data() {
    return {
      error: null,
      showErrors: false,
      inputValidations: InputValidations,
      model: defaultModel,
      Phone: [
        { text: 'Mobile', value: 'mobile', formattedName: 'Mobile' },
        { text: 'Work', value: 'work', formattedName: 'Work' },
        { text: 'Home', value: 'home', formattedName: 'Home' },
      ],
      Country: [
        { text: 'CA', value: 'CA' },
        { text: 'US', value: 'US' },
      ],
      loading: false,
    };
  },
  props: {
    reDirRouteName: {
      type: String,
      default: '',
    },
    existingQueryParams: {
      type: String,
      default: '',
    },
    accountType: {
      type: String,
      default: 'INSPECTOR',
    },
  },
  created() {
    this.setAccountType();
    const previousHost = document.referrer ? new URL(document.referrer).host : '';
    const currentHost = window.location.host;
    if (!this.existingQueryParams && (!previousHost || previousHost === currentHost)) {
      window.location = new URL(process.env.VUE_APP_PRICING_URL);
    }
  },
  computed: {
    showApiErrors() {
      return !!(this.error);
    },
    showExistingUserError() {
      return !!(this.existingUserError);
    },
    isFormInvalid() {
      return this.$validator.errors.items.length > 0
        || (this.model.user.password !== this.model.user.verifyPassword)
        || this.loading
        || this.existingUserError
        || this.error;
    },
  },
  methods: {
    clearApiErrors() {
      this.error = null;
      this.existingUserError = null;
    },
    async onFormSubmit(event) {
      event.preventDefault();
      if (this.loading) {
        return;
      }
      this.clearApiErrors();
      await this.$validator.validateAll();

      if (!this.errors.any() && !this.error) {
        this.loading = true;
        this.createUser();
      } else {
        this.$scrollTo('#messages');
      }
    },
    createUser() {
      this.loading = true;
      this.$store.dispatch('Person/createUserAccount', {
        user: this.model.user,
      })
        .then(res => this.authenticateUser(res))
        .catch(err => {
          this.loading = false;
          this.error = err ? err.body : err;
          this.$scrollTo('#messages');
        });
    },
    async authenticateUser(res) {
      await this.$store.dispatch('Auth/loginUser', {
        username: res.username,
        password: this.model.user.password,
      });
      this.loading = false;
      this.redirectToChargeBeeCheckout();
    },
    redirectToChargeBeeCheckout() {
      this.loading = false;
      if (this.existingQueryParams) {
        this.$router.push({
          path: 'chargebee',
          query: {
            redirectParams: this.existingQueryParams,
          },
        });
      }
    },
    redirectToLogin() {
      this.loading = false;
      this.$router.push({
        path: 'login',
        query: {
          reDirRouteName: 'chargebee',
          existingQueryParams: this.existingQueryParams,
        },
      });
    },
    isUserEmailExists() {
      if (!this.model.user.email) {
        return;
      }
      this.clearApiErrors();
      this.loading = true;
      this.$store.dispatch('Person/checkByEmailAccountType', {
        email: this.model.user.email,
        type: this.model.user.accountType,
      })
        .then(res => this.getToken(res))
        .catch(() => {
          this.loading = false;
          // eat the error, we anticipate a possible 401/3
        });
    },
    getToken(res) {
      this.error = !res?.token;
      this.existingUserError = this.error ? 'We found an existing account associated with your email address.' : null;
      this.model.user.token = res.token;
      this.loading = false;
    },
    setAccountType() {
      if (this.accountType === AccountTypes.REAL_ESTATE_PROFESSIONAL || this.accountType === 'REP') {
        this.model.user.accountType = this.accountType;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/vars/breakpoints';
.sign-up-container {
  padding: 0px;

  & .sub-header-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 24px;
    @include breakpoint(sm) {
      flex-direction: row;
    }

    & .sub-title {
      text-align: center;
      color: #000;
      font-size: 16px;
      font-weight: 500;
      @include breakpoint(sm) {
        padding-right: 15px;
      }
    }
    & .login-title {
      font-size: 16px;
      font-weight: 500;
      text-align: center;
      color: #f7942f;
      cursor: pointer;
    }
  }

  & .divider-line {
    margin: 40px 0px;
    border-bottom: 1px solid #d9d9d1;
  }
  & .divider-line-top {
    margin: 40px 0px 0px 0px;
    border-bottom: 1px solid #d9d9d1;
  }
  & .form-container {
    & .error-alerts {
      margin-top: 15px;
      & .error-message {
        word-break: break-all;
      }
    }
    & .form-title {
      font-size: 24px;
      color: #000;
      padding-top:40px;
    }
    & .input-holder {
      padding-top:16px;
    }
    & .button-container {
      display: flex;
      justify-content: center;
      margin-top: 40px;

      @include breakpoint(sm) {
        justify-content: flex-end;
      }
      & .continue-button {
        width: 100%;
        @include breakpoint(sm) {
          width: 50%;
        }
      }
    }
    & .confirm-password {
      & .password-toggle {
        display: none;
      }
    }
  }
}
</style>
